import { keccak256, toBytes, type Address, type Hex, type WalletClient } from "viem";
import { config } from "../config";
import { l2Public } from "./chain";
import { marketAbi } from "../abi";
import {
  newNonce,
  preauthSentinel,
  serializeReceipt,
  signReceipt,
  type Receipt,
} from "../receipt";

export interface DiscoveredMiner {
  address: Address;
  brainId: number;
  url?: string;
  score?: number;
}

export interface InferenceResult {
  text: string;
  miner: Address;
  responseHash: Hex;
  tokensIn: number;
  tokensOut: number;
  /** What the miner will actually be able to draw (the counter-signed final charge, or the pre-auth
   *  fallback if settlement never happened). */
  charged: bigint;
  settled: boolean;
}

export async function listMiners(brainId: number): Promise<DiscoveredMiner[]> {
  const res = await fetch(`${config.gatewayUrl.replace(/\/$/, "")}/brains/${brainId}/miners`);
  if (!res.ok) return [];
  return (await res.json()) as DiscoveredMiner[];
}

async function readPricing(): Promise<[bigint, bigint]> {
  const market = config.l2.contracts.market;
  const ppt = (await l2Public.readContract({ address: market, abi: marketAbi, functionName: "pricePerByte" })) as bigint;
  const ppr = (await l2Public.readContract({ address: market, abi: marketAbi, functionName: "pricePerRequest" })) as bigint;
  return [ppt, ppr];
}

/**
 * Run one inference end-to-end, mirroring the reference client's payment protocol:
 *   1. sign a bounded pre-authorization (sentinel responseHash, capped at maxReservation on-chain),
 *   2. POST it + the prompt to the gateway, which proxies to the highest-scored miner,
 *   3. verify the delivered text hashes to the committed responseHash,
 *   4. independently re-derive the metered charge and counter-sign the FINAL receipt (only this
 *      authorizes the real cost), then settle it.
 * Every signature is produced by the connected injected wallet.
 */
export async function runInference(params: {
  wallet: WalletClient;
  user: Address;
  brainId: number;
  prompt: string;
  budget: bigint;
  reservation: bigint;
  maxTokens?: number;
  temperature?: number;
}): Promise<InferenceResult> {
  const { wallet, user, brainId, prompt, budget } = params;
  const market = config.l2.contracts.market;
  const chainId = config.l2.chainId;

  const miners = await listMiners(brainId);
  if (miners.length === 0) throw new Error(`No miners available for Brain ${brainId}`);
  const miner = [...miners].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0];

  const promptHash = keccak256(toBytes(prompt));
  const expiry = Math.floor(Date.now() / 1000) + 3600;
  const nonce = newNonce();
  let reservation = params.reservation > 0n ? params.reservation : budget;
  if (reservation > budget) reservation = budget;

  const preauth: Receipt = {
    user,
    miner: miner.address,
    brainId,
    promptHash,
    responseHash: preauthSentinel(promptHash, nonce),
    amount: reservation,
    expiry,
    nonce,
  };
  const preauthSig = await signReceipt(wallet, market, chainId, preauth);

  const res = await fetch(`${config.gatewayUrl.replace(/\/$/, "")}/brains/${brainId}/infer`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      prompt,
      max_tokens: params.maxTokens ?? 256,
      temperature: params.temperature ?? 0.7,
      budget: budget.toString(),
      receipt: serializeReceipt(preauth),
      user_signature: preauthSig,
      target_miner: miner.address,
    }),
  });
  if (!res.ok) throw new Error(`Inference failed: ${res.status} ${await res.text()}`);

  const data = (await res.json()) as {
    text: string;
    response_hash: Hex;
    tokens_in: number;
    tokens_out: number;
    final_receipt?: {
      user: Hex; miner: Hex; brain_id: number; prompt_hash: Hex;
      response_hash: Hex; amount: string; expiry: number; nonce: string;
    };
    error?: string;
  };
  if (data.error) throw new Error(data.error);

  // Verify the delivered output matches the committed hash BEFORE authorizing full payment.
  if (keccak256(toBytes(data.text)).toLowerCase() !== data.response_hash.toLowerCase()) {
    throw new Error("Response hash does not match returned text — refusing to pay.");
  }

  let charged = reservation;
  let settled = false;
  if (data.final_receipt) {
    const fr = data.final_receipt;
    const finalReceipt: Receipt = {
      user: fr.user, miner: fr.miner, brainId: fr.brain_id, promptHash: fr.prompt_hash,
      responseHash: fr.response_hash, amount: BigInt(fr.amount), expiry: fr.expiry, nonce: BigInt(fr.nonce),
    };
    // The final receipt must share the pre-auth's identity and bind the delivered output.
    if (
      finalReceipt.user.toLowerCase() !== preauth.user.toLowerCase() ||
      finalReceipt.miner.toLowerCase() !== preauth.miner.toLowerCase() ||
      finalReceipt.brainId !== preauth.brainId ||
      finalReceipt.promptHash.toLowerCase() !== preauth.promptHash.toLowerCase() ||
      finalReceipt.expiry !== preauth.expiry ||
      finalReceipt.nonce !== preauth.nonce
    ) {
      throw new Error("Final receipt identity does not match the pre-authorization.");
    }
    if (finalReceipt.responseHash.toLowerCase() !== data.response_hash.toLowerCase()) {
      throw new Error("Final receipt does not bind the delivered response.");
    }
    if (finalReceipt.amount > budget) throw new Error("Final charge exceeds budget.");

    // Re-derive the metered charge from UTF-8 byte lengths, independently of the miner.
    const [ppt, ppr] = await readPricing();
    if (ppt <= 0n && ppr <= 0n) throw new Error("Market pricing is disabled.");
    const inBytes = BigInt(new TextEncoder().encode(prompt).length);
    const outBytes = BigInt(new TextEncoder().encode(data.text).length);
    const quoted = ppr + ppt * (inBytes + outBytes);
    const expected = quoted < budget ? quoted : budget;
    if (finalReceipt.amount !== expected) {
      throw new Error(`Incorrect metered charge: got ${finalReceipt.amount}, expected ${expected}.`);
    }

    const finalSig = await signReceipt(wallet, market, chainId, finalReceipt);
    charged = finalReceipt.amount;
    try {
      await fetch(`${config.gatewayUrl.replace(/\/$/, "")}/brains/${brainId}/settle`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ receipt: serializeReceipt(finalReceipt), user_signature: finalSig }),
      });
      settled = true;
    } catch {
      // Settlement is best-effort; the signed pre-auth remains the miner's bounded fallback.
    }
  }

  return {
    text: data.text,
    miner: miner.address,
    responseHash: data.response_hash,
    tokensIn: data.tokens_in,
    tokensOut: data.tokens_out,
    charged,
    settled,
  };
}
